import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormProvider,
  useForm,
  type FieldValues,
  type SubmitHandler,
  type UseFormProps,
  type UseFormReturn,
  type UseFormSetError,
} from 'react-hook-form';
import type { z } from 'zod';

import { ApiError } from '../../lib/api';

/**
 * Forms on React Hook Form, validated by a Zod schema. The fields themselves
 * are `Field` with a `name` (`./field`).
 *
 *   const form = useZodForm(contactSchema, { defaultValues: { fullName: '', … } });
 *
 *   <Form form={form} onSubmit={(values) => sending.mutate(values)}>
 *     <Field name="fullName" label="Full name" required>
 *       <Input autoComplete="name" />
 *     </Field>
 *   </Form>
 *
 * Timing follows Field's rule: a field is checked when it is left, never on the
 * first keystroke, and re-checked as you type once it has been. Submitting
 * checks everything and moves focus to the first field that is wrong.
 */

/**
 * `useForm` with the schema as its resolver. Values are typed from the
 * schema's input; the submit handler receives its parsed output, so trims and
 * transforms have already run.
 */
export function useZodForm<TInput extends FieldValues, TOutput extends FieldValues>(
  schema: z.ZodType<TOutput, TInput>,
  options: Omit<UseFormProps<TInput, unknown, TOutput>, 'resolver'> = {},
): UseFormReturn<TInput, unknown, TOutput> {
  return useForm<TInput, unknown, TOutput>({
    mode: 'onTouched',
    ...options,
    resolver: zodResolver(schema),
  });
}

/**
 * The `<form>` and the context its fields read. `noValidate` because the
 * schema is the validator — the browser's own bubbles would race it.
 */
export function Form<TValues extends FieldValues, TOutput>({
  form,
  onSubmit,
  className,
  children,
}: {
  form: UseFormReturn<TValues, unknown, TOutput>;
  onSubmit: SubmitHandler<TOutput>;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={form.handleSubmit(onSubmit)} className={className}>
        {children}
      </form>
    </FormProvider>
  );
}

/** One issue from the API's `400 VALIDATION_ERROR`, as its error handler sends them. */
type ServerIssue = { path: (string | number)[]; message: string };

const isServerIssue = (value: unknown): value is ServerIssue =>
  typeof value === 'object' &&
  value !== null &&
  Array.isArray((value as ServerIssue).path) &&
  typeof (value as ServerIssue).message === 'string';

/**
 * Puts the API's validation errors under the fields they name. Returns whether
 * any were placed — when none were, the error is the form's to show as a whole.
 *
 * Only reached when the client's schema let something through that the
 * server's did not, so the message is the server's wording.
 */
export function applyServerErrors<TValues extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<TValues>,
  /** Where an event form's answers live, for the `…_ANSWER` refusals. */
  answersName = 'answers',
): boolean {
  if (!(error instanceof ApiError)) return false;

  // An event form's own question, refused: `details.questionId` names it, and
  // the question is bound at `<answersName>.<id>` (see FormQuestions). The
  // field already shows the label, so the message does not repeat it.
  const answerCode = /_(REQUIRED|INVALID)_ANSWER$/.exec(error.code ?? '');
  const questionId = (error.body?.details as { questionId?: unknown } | undefined)?.questionId;
  if (answerCode && typeof questionId === 'string') {
    setError(
      `${answersName}.${questionId}` as Parameters<UseFormSetError<TValues>>[0],
      {
        type: 'server',
        message:
          answerCode[1] === 'REQUIRED'
            ? 'This question needs an answer.'
            : "This answer doesn't fit the question. Check it and try again.",
      },
      { shouldFocus: true },
    );
    return true;
  }

  if (error.code !== 'VALIDATION_ERROR') return false;
  const details = error.body?.details;
  const placed = (Array.isArray(details) ? details.filter(isServerIssue) : []).filter(
    (issue) => issue.path.length > 0,
  );
  placed.forEach((issue, i) =>
    setError(
      issue.path.join('.') as Parameters<UseFormSetError<TValues>>[0],
      { type: 'server', message: issue.message },
      { shouldFocus: i === 0 },
    ),
  );
  return placed.length > 0;
}
